import { useState, useEffect } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';
import { useGlobalDialog } from '../../Store/DialogManager';
import styled from 'styled-components';
import { Card } from "@mui/material";
import { useSnackbar } from 'notistack';
import {
    IconActivityHeartbeat,
    IconAlertTriangle,
    IconArrowBarToUp,
    IconCaretLeftRight,
    IconChevronLeft,
    IconCircleCheck,
    IconClock,
    IconDatabase,
    IconDeviceFloppy,
    IconRefresh,
    IconServer,
    IconSettings,
    IconShieldCheck,
    IconSquareRoundedX,
} from '@tabler/icons-react';

import {
    AutoBackupHeaderContainer,
    DbInstancesPanelContainer,
    FieldContainer,
    InstanceDetailsPanelContainer,
    InstanceOtherDetailsContainer,
    InstanceStatusContainer,
    InstanceStatusesContainer,
    SchemaIntegrityItem,
    SummaryCountItem,
    SummaryCountsContainer,
    TwoItemsContainer,
} from '../../Styles/InstanceManagement/instanceManagementStyles';

import QueryBuilderHelper from '../../Utilities/Helpers/QueryBuilderHelper';
import GlobalFunctionHelper from '../../Utilities/Helpers/GlobalFunctionHelper';
import LocalStorageHelper from '../../Utilities/Helpers/LocalStorageHelper';
import DateHelper from '../../Utilities/Helpers/DateHelper';
import { DbCloneIcon } from '../../Utilities/Helpers/IconHelper';

import { InstanceAutoBackupsField, InstanceManagementField, InstanceManagementListField } from '../../Utilities/Enums/ParametersEnum';
import { OrderByEnum } from '../../Utilities/Enums/SortEnums';
import { CachingEnum } from '../../Utilities/Enums/CachingEnum';
import { RouteEnum } from '../../Utilities/Enums/RouteEnums';
import { ButtonType } from '../../Utilities/Enums/ButtonTypeEnum';
import { DBBackupStatusEnum, DBBackupStatusTextEnum, InstanceStatusEnum, InstanceStatusTextEnum, SchemaHealthStatusEnum, SchemaHealthStatusTextEnum } from '../../Utilities/Enums/StatusEnums';

import { ErrorMessage, SuccessMessage } from '../../Constants/Messages';

import CustomDetailsCard from '../../CustomViews/Pages/CustomDetailsCard/CustomDetailsCard';
import CustomButton from '../../CustomViews/Elements/CustomButton';
import CustomSwitch from '../../CustomViews/Elements/CustomSwitch';
import MaintenanceModeConfirmationModal from './MaintenanceModeConfirmationModal';
import InstanceStoredProcedures from './InstanceStoredProcedures';
import InstanceActivityLogs from './InstanceActivityLogs';
import BackupConfigModal from './BackupConfigModal';
import AutoBackups from './AutoBackups';
import DBInstances from './DBInstances';
import DBCloneModal from './DBCloneModal';

import { DBInstanceClone, InstanceAutoBackupConfig, InstanceMngt } from '../../Models/InstanceManagement/InstanceManagement';

import { useInstanceManagementHook } from '../../Hooks/InstanceManagement/instanceManagementHooks';

const DetailsContainer = styled(Card) <{ $darkMode?: boolean; }>`
    background: transparent !important;
    border: none;
    border-radius: 0px !important;
    display: grid;
    grid-template-columns: auto;
    gap: 30px;
    box-shadow: none !important;
    padding: 10px 0px;
    overflow: visible !important;
`;

export default function InstanceManagementDetails() {
    // Variables
    let navigate = useNavigate();
    let location = useLocation();
    let queryParams = new URLSearchParams(location.search);
    let instanceId = queryParams.get('id') ?? "0";
    let isDarkMode = LocalStorageHelper.isDarkMode();
    const { setDialogContent, setOpenGlobalDialog } = useGlobalDialog();
    const { enqueueSnackbar } = useSnackbar();

    // Use States
    const [queryStr, setQueryStr] = useState({
        pageSize: QueryBuilderHelper.DefaultPageSizeValue(),
        pageNumber: QueryBuilderHelper.DefaultPageNumValue(),
        searchString: QueryBuilderHelper.DefaultSearchValue(),
        status: QueryBuilderHelper.DefaultQueryFilterValue('status') ?? 0,
        sortBy: QueryBuilderHelper.DefaultSortValue(InstanceManagementListField.CREATED_DATE),
        orderBy: QueryBuilderHelper.DefaultOrderValue(OrderByEnum.DESC),
    });
    let [previewData, setpreviewData] = useState<InstanceMngt>(new InstanceMngt());
    let [isMaintenanceMode, setisMaintenanceMode] = useState<boolean>(false);
    let [isEnabledAutoBackup, setisEnabledAutoBackup] = useState<boolean>(true);
    let [openMaintenanceModeConfirmationModal, setopenMaintenanceModeConfirmationModal]: any = useState<boolean>(false);
    let [autoBackupConfig, setautoBackupConfig] = useState<InstanceAutoBackupConfig>(new InstanceAutoBackupConfig());
    let [previewAutoBackupConfig, setpreviewAutoBackupConfig] = useState<InstanceAutoBackupConfig>(new InstanceAutoBackupConfig());
    let [submitForm, setsubmitForm] = useState<boolean>(false);
    let [openBackupConfigModal, setopenBackupConfigModal] = useState<boolean>(false);
    let [openDBCloneModal, setopenDBCloneModal] = useState<boolean>(false);
    let [triggeredQuery, settriggeredQuery] = useState<any>('all');

    const q_queryKey = [CachingEnum.INSTANCE_MANAGEMENT_LIST, queryStr]
    const { DropdownData, dropdownFetchStatus, getInstanceById, instanceRefreshById, instanceBackUpNowById, instanceSchemaCheckById, getBackupConfigById, editBackupConfigById, instancesInvalidateQueries, dbCloneCheckById, dbCloneById } = useInstanceManagementHook(q_queryKey, queryStr, false);

    //Event Handlers
    const getInstanceData = async () => {
        if(instanceId != '0') {
            let getDetails = await getInstanceById(instanceId);
            let _previewData = {...previewData};

            if(getDetails?.data) {
                _previewData = {
                    ...previewData,
                    ...getDetails.data,
                };
            }

            setpreviewData(_previewData);
        }
    }

    const getBackupConfigData = async () => {
        if(instanceId != '0') {
            let getDetails = await getBackupConfigById(instanceId);
            let _autoBackupConfig = {...autoBackupConfig};

            if(getDetails?.data) {
                _autoBackupConfig = {
                    ...autoBackupConfig,
                    ...getDetails.data,
                    [InstanceAutoBackupsField.DESTINATION]: 'Local disk (on-premise)',
                };
            }

            setautoBackupConfig(_autoBackupConfig);
            setpreviewAutoBackupConfig(_autoBackupConfig);
        }
    }

    const runHealthCheck = async () => {
        if(instanceRefreshById && instanceId) {
            let refreshInstanceApiResponse = await instanceRefreshById(instanceId, true);

            if(refreshInstanceApiResponse?.isSuccess) {
                getInstanceData();
            }
        }
    }

    const backupNow = async () => {
        if(instanceBackUpNowById && instanceId) {
            let backUpNowInstanceApiResponse = await instanceBackUpNowById(instanceId, true);

            if(backUpNowInstanceApiResponse?.isSuccess) {
                getInstanceData();
            }
        }
    }

    const reRunSchemaCheck = async () => {
        if(instanceSchemaCheckById && instanceId) {
            let schemaCheckInstanceApiResponse = await instanceSchemaCheckById(instanceId, true);

            if(schemaCheckInstanceApiResponse?.isSuccess) {
                getInstanceData();
            }
        }
    }

    const handleSubmitBackupConfig = async () => {
        if(instanceId) {
            settriggeredQuery(CachingEnum.INSTANCE_AUTO_BACKUP_LIST);

            let editResponse = await editBackupConfigById(instanceId, JSON.stringify(autoBackupConfig));
            let _success: boolean = editResponse?.isSuccess ?? false;

            if(_success) {
                getInstanceData();
                getBackupConfigData();
                
                onCloseDialog();
            }
        }

        setsubmitForm(false);
    }

    const handleCloneDB = async (cloneForm: DBInstanceClone) => {
        if(instanceId) {
            settriggeredQuery(CachingEnum.DB_INSTANCES_LIST);
            
            let cloneCheckResponse = await dbCloneCheckById(instanceId);

            if(cloneCheckResponse?.data?.canClone) {
                enqueueSnackbar(cloneCheckResponse?.message ?? SuccessMessage.READY_TO_CLONE, { variant: 'success' })

                let cloneResponse = await dbCloneById(instanceId, JSON.stringify(cloneForm));

                if(cloneResponse?.isSuccess) {
                    onCloseDialog();
                    await instancesInvalidateQueries([CachingEnum.DB_INSTANCES_LIST]);
                }
            } else {
                enqueueSnackbar((cloneCheckResponse?.message ?? ErrorMessage.PROBLEM_DB_CLONE), { variant: 'error' })
            }
        }
    }

    const onCloseDialog = async () => {
        setopenBackupConfigModal(false);
        setopenDBCloneModal(false);
        setOpenGlobalDialog(false);
    }

    useEffect(() => {
            getInstanceData();
            getBackupConfigData();
    }, [instanceId]);

    useEffect(() => {
        if(submitForm) {
            handleSubmitBackupConfig();
        }
    }, [submitForm]);
    
    //Rendering Functions
    //INSTANCE DETAILS
    const renderActionButtons = () => {
        return <>
            <CustomButton
                btnType={ButtonType.CANCEL}
                title='Back'
                startIcon={<IconChevronLeft />}
                onClick={(e: any) => {
                    GlobalFunctionHelper.goBack(navigate, location, RouteEnum.INSTANCE_MANAGEMENT);
                }} />
            
            <CustomButton
                btnType={ButtonType.PRIMARY_LIGHT}
                title='Run Health Check'
                startIcon={<IconActivityHeartbeat />}
                // isRoundedCorner={true}
                onClick={(e: any) => {
                    runHealthCheck();
                }} />
            
            <CustomButton
                btnType={ButtonType.PRIMARY}
                title='Backup Now'
                startIcon={<IconDeviceFloppy />}
                // isRoundedCorner={true}
                onClick={(e: any) => {
                    backupNow();
                }} />
        </>
    }

    const renderInstanceHeaderDetails = () => {
        return <InstanceDetailsPanelContainer>
            <IconServer />
            <div className='instanceHeaderDetails'>
                <span className='instanceName'>{previewData?.[InstanceManagementField.NAME] ?? "--"}</span>
                <span className='companyName'>{previewData?.[InstanceManagementField.COMPANY_NAME] ?? "--"} . {previewData?.[InstanceManagementField.IS_ON_PREMISE] ? "On-premise" : "Cloud"} instance</span>
            </div>
        </InstanceDetailsPanelContainer>
    }

    const renderInstanceOtherDetails = () => {
        let status: InstanceStatusEnum = previewData?.[InstanceManagementField.STATUS] <= 0 ? InstanceStatusEnum.OFFLINE : previewData?.[InstanceManagementField.STATUS];
        let statusText = InstanceStatusTextEnum[status];
        let dbVersionNum = previewData?.[InstanceManagementField.DB_VERSION] != null && previewData?.[InstanceManagementField.DB_VERSION] != "" ? previewData?.[InstanceManagementField.DB_VERSION] : "--";
        let buildNum = previewData?.[InstanceManagementField.BUILD_VERSION] != null && previewData?.[InstanceManagementField.BUILD_VERSION] != "" ? previewData?.[InstanceManagementField.BUILD_VERSION] : "--";

        return <InstanceOtherDetailsContainer $darkMode={isDarkMode} sx={{borderColor: 'customBorder.main'}}>
            <span className={'connectionStatus ' + statusText.toLowerCase()}>{statusText}</span>
            
            {isMaintenanceMode && <span className='maintenanceModeText'>
                <span className='dot'>&nbsp;</span>
                MAINTENANCE · DEBUG CLONE ACTIVE
            </span> }

            <span className='engineText'>
                <IconServer />
                {previewData?.[InstanceManagementField.ENGINE] ?? "--" }
            </span>

            <span className='versionBuildText'>v {dbVersionNum} . build {buildNum}</span>

            {/* <div className='switchContainer'>
                <div className='switchDetails'>
                    <span className='switchTitle'>Maintenance mode</span>
                    <span className='switchNote'>Production stays live · clones for debug</span>
                </div>
                <CustomSwitch
                name='isMaintenanceMode'
                title=""
                switchType={ButtonType.PRIMARY}
                fieldValue={isMaintenanceMode ?? false}
                handleOnChange={(a: any) => {
                    if(a) {
                        setopenMaintenanceModeConfirmationModal(a);
                    } else {
                        setisMaintenanceMode(a);
                    }
                }}
                errorMessages={[]} />
            </div> */}
        </InstanceOtherDetailsContainer>
    }

    const renderSummaryCounts = () => {
        let status: InstanceStatusEnum = previewData?.[InstanceManagementField.STATUS] <= 0 ? InstanceStatusEnum.OFFLINE : previewData?.[InstanceManagementField.STATUS];
        let statusText = InstanceStatusTextEnum[status];
        let dbVersionNum = previewData?.[InstanceManagementField.DB_VERSION] != null && previewData?.[InstanceManagementField.DB_VERSION] != "" ? previewData?.[InstanceManagementField.DB_VERSION] : "--";
        let buildNum = previewData?.[InstanceManagementField.BUILD_VERSION] != null && previewData?.[InstanceManagementField.BUILD_VERSION] != "" ? previewData?.[InstanceManagementField.BUILD_VERSION] : "--";
        let latencyMS = previewData?.[InstanceManagementField.LATENCY_MS] ?? 0;
        let lastCheckedAt: string | Date | null = previewData?.[InstanceManagementField.LAST_CHECKED_AT] != null && previewData?.[InstanceManagementField.LAST_CHECKED_AT] != "" ? DateHelper.getFormattedDate(previewData?.[InstanceManagementField.LAST_CHECKED_AT], "dd/MM/yyyy hh:mm a") : null;

        return <SummaryCountsContainer $darkMode={isDarkMode} sx={{borderColor: 'customBorder.main'}}>
            <SummaryCountItem $darkMode={isDarkMode} sx={{borderColor: 'customBorder.main'}}>
                <span className='summaryItemTitle'>Active Connections</span>
                <span className='summaryItemValue'><span className='itemVal'>{previewData?.[InstanceManagementField.ACTIVE_CONNECTIONS] ?? 0}</span> / {previewData?.[InstanceManagementField.MAX_CONNECTIONS] ?? 0}</span>
            </SummaryCountItem>

            <SummaryCountItem $darkMode={isDarkMode} sx={{borderColor: 'customBorder.main'}}>
                <span className='summaryItemTitle'>Latency</span>
                <span className='summaryItemValue siv latencyText'>{latencyMS > 0 ? latencyMS + " ms" : '—'}</span>
            </SummaryCountItem>
            
            <SummaryCountItem $darkMode={isDarkMode} sx={{borderColor: 'customBorder.main'}}>
                <span className='summaryItemTitle'>Database Size</span>
                <span className='summaryItemValue'><span className='itemVal'>{previewData?.[InstanceManagementField.DATABASE_SIZE_GB] ?? 0}</span> / {previewData?.[InstanceManagementField.STORAGE_CAPACITY_GB] ?? 0} GB</span>
            </SummaryCountItem>

            <SummaryCountItem $darkMode={isDarkMode} sx={{borderColor: 'customBorder.main'}}>
                <span className='summaryItemTitle'>Last Health Check</span>
                <span className='summaryItemValue siv'>{lastCheckedAt ?? "—"}</span>
            </SummaryCountItem>
        </SummaryCountsContainer>
    }

    const renderInstanceStatuses = () => {
        let schemaHealth: SchemaHealthStatusEnum = previewData?.[InstanceManagementField.SCHEMA_HEALTH] ?? SchemaHealthStatusEnum.HEALTHY;
        let schemaHealthText = SchemaHealthStatusTextEnum[schemaHealth];
        let dbBackupStatus: DBBackupStatusEnum | SchemaHealthStatusEnum = previewData?.[InstanceManagementField.BACKUP_STATUS] ?? DBBackupStatusEnum.ENABLED;
        let dbBackupStatusText = DBBackupStatusTextEnum[dbBackupStatus];
        let pendingMigrations: number = previewData?.[InstanceManagementField.PENDING_MIGRATIONS] ?? 0;

        if(dbBackupStatus == DBBackupStatusEnum.ENABLED && schemaHealth != SchemaHealthStatusEnum.HEALTHY) {
            dbBackupStatus = schemaHealth;
            dbBackupStatusText = schemaHealthText;
        }

        return <InstanceStatusesContainer $darkMode={isDarkMode} sx={{borderColor: 'customBorder.main'}}>
            <InstanceStatusContainer $darkMode={isDarkMode} sx={{borderColor: 'customBorder.main'}}>
                <span className={'statusItemText ' + schemaHealthText.toLowerCase()}>
                    <span className='dot'>&nbsp;</span>
                    Schema
                </span>

                <span className='statusText'>{schemaHealth == SchemaHealthStatusEnum.HEALTHY ? 'OK' : (schemaHealth == SchemaHealthStatusEnum.DRIFT ? "Attention" : schemaHealthText)}</span>
            </InstanceStatusContainer>

            <InstanceStatusContainer $darkMode={isDarkMode} sx={{borderColor: 'customBorder.main'}}>
                <span className={'statusItemText ' + schemaHealthText.toLowerCase()}>
                    <span className='dot'>&nbsp;</span>
                    Stored Procs
                </span>

                <span className='statusText'>{schemaHealth == SchemaHealthStatusEnum.HEALTHY ? 'OK' : (schemaHealth == SchemaHealthStatusEnum.DRIFT ? "Attention" : schemaHealthText)}</span>
            </InstanceStatusContainer>

            <InstanceStatusContainer $darkMode={isDarkMode} sx={{borderColor: 'customBorder.main'}}>
                <span className={'statusItemText ' + dbBackupStatusText.toLowerCase()}>
                    <span className='dot'>&nbsp;</span>
                    BackUps
                </span>

                <span className='statusText'>{dbBackupStatus == DBBackupStatusEnum.ENABLED ? 'OK' : (dbBackupStatusText == SchemaHealthStatusTextEnum[SchemaHealthStatusEnum.DRIFT] ? "Attention" : dbBackupStatusText)}</span>
            </InstanceStatusContainer>

            <InstanceStatusContainer $darkMode={isDarkMode} sx={{borderColor: 'customBorder.main'}}>
                <span className={'statusItemText ' + (pendingMigrations > 0 ? "hasPendingMigrations" : "noPendingMigrations")}>
                    <span className='dot'>&nbsp;</span>
                    Version
                </span>
                
                <span className='statusText'>{(pendingMigrations > 0 ? pendingMigrations + " pending" : "Up to date")}</span>
            </InstanceStatusContainer>
        </InstanceStatusesContainer>
    }

    const renderMaintenanceModeConfirmationModal = () => {
        return <MaintenanceModeConfirmationModal
            openDialog={openMaintenanceModeConfirmationModal}
            handleClose={() => { setopenMaintenanceModeConfirmationModal(false); }}
            buttonAction={() => {
                setisMaintenanceMode(true);
                setopenMaintenanceModeConfirmationModal(false);
            }}
            instanceName={previewData?.[InstanceManagementField.NAME] != null && previewData?.[InstanceManagementField.NAME] != "" ? 
                previewData?.[InstanceManagementField.NAME] : null
            } />
    }
    ////////////////////////////////////////////////////////////////////////////////////////////////////

    //DB VERSIONS AND MIGRATIONS
    const renderDBVersionAndMigrationsHeader = () => {
        return <InstanceDetailsPanelContainer>
            <IconClock />
            <div className='instanceHeaderDetails'>
                <span className='instanceName'>DB Version & Migrations</span>
            </div>
        </InstanceDetailsPanelContainer>
    }

    const renderDBVersionAndMigrationsActionBtns = () => {
        let pendingMigrations: number = previewData?.[InstanceManagementField.PENDING_MIGRATIONS] ?? 0;

        return <>
            {pendingMigrations <= 0 && <InstanceStatusContainer $darkMode={isDarkMode} sx={{borderColor: 'customBorder.main'}} $noborderAndPadding={true}>
                <span className={'statusItemText ' + (pendingMigrations > 0 ? "hasPendingMigrations" : "noPendingMigrations")}>
                    <span className='dot'>&nbsp;</span>
                    {(pendingMigrations > 0 ? pendingMigrations + " pending" : "Up to date")}
                </span>
            </InstanceStatusContainer> }

            {pendingMigrations > 0 && <CustomButton
                btnType={ButtonType.PRIMARY}
                title='Apply Updates'
                startIcon={<IconArrowBarToUp />}
                onClick={(e: any) => {
                    
                }} /> }
        </>
    }

    const renderDBVersionAndMigrationsDetails = () => {
        let dbVersionNum = previewData?.[InstanceManagementField.DB_VERSION] != null && previewData?.[InstanceManagementField.DB_VERSION] != "" ? previewData?.[InstanceManagementField.DB_VERSION] : null;
        let buildNum = previewData?.[InstanceManagementField.BUILD_VERSION] != null && previewData?.[InstanceManagementField.BUILD_VERSION] != "" ? previewData?.[InstanceManagementField.BUILD_VERSION] : "--";
        let lastMigrationDate: string | Date | null = previewData?.[InstanceManagementField.LAST_MIGRATION_DATE] != null && previewData?.[InstanceManagementField.LAST_MIGRATION_DATE] != "" ? DateHelper.getFormattedDate(previewData?.[InstanceManagementField.LAST_MIGRATION_DATE], "dd/MM/yyyy") : null;

        return <FieldContainer $darkMode={isDarkMode} sx={{borderColor: 'customBorder.main'}}>
            <div className='fieldItemContainer'>
                <span className='fieldHeader'>Application Version</span>
                <span className='fieldValue'>{dbVersionNum ? 'v' + dbVersionNum : '--'}</span>
            </div>
            <div className='fieldItemContainer'>
                <span className='fieldHeader'>Build</span>
                <span className='fieldValue'>{buildNum}</span>
            </div>

            <div className='fieldItemContainer'>
                <span className='fieldHeader'>Schema Revision</span>
                <span className='fieldValue'>{previewData?.[InstanceManagementField.SCHEMA_REVISON] != null && previewData?.[InstanceManagementField.SCHEMA_REVISON] != "" ? previewData?.[InstanceManagementField.SCHEMA_REVISON] : "--"}</span>
            </div>
            <div className='fieldItemContainer'>
                <span className='fieldHeader'>Engine</span>
                <span className='fieldValue'>
                    {previewData?.[InstanceManagementField.ENGINE] != null && previewData?.[InstanceManagementField.ENGINE] != "" ? previewData?.[InstanceManagementField.ENGINE] : "--"}
                    {previewData?.[InstanceManagementField.IS_ON_PREMISE] && " (on-premise)"}
                </span>
            </div>

            <div className='fieldItemContainer'>
                <span className='fieldHeader'>Last Migration</span>
                <span className='fieldValue'>
                    {previewData?.[InstanceManagementField.LAST_MIGRATION_NAME] != null && previewData?.[InstanceManagementField.LAST_MIGRATION_NAME] != "" ? previewData?.[InstanceManagementField.LAST_MIGRATION_NAME] : "--"} . {lastMigrationDate ?? "--"}
                </span>
            </div>
        </FieldContainer>
    }
    ////////////////////////////////////////////////////////////////////////////////////////////////////

    //SCHEMA INTEGRITY
    const renderSchemaIntegrityHeader = () => {
        return <InstanceDetailsPanelContainer>
            <IconShieldCheck />
            <div className='instanceHeaderDetails'>
                <span className='instanceName'>Schema Integrity</span>
            </div>
        </InstanceDetailsPanelContainer>
    }

    const renderSchemaIntegrityActionBtns = () => {
        let warningCount: number = 0;

        return <>
            <InstanceStatusContainer $darkMode={isDarkMode} sx={{borderColor: 'customBorder.main'}} $noborderAndPadding={true}>
                <span className={'statusItemText ' + (warningCount > 0 ? "hasPendingMigrations" : "noPendingMigrations")}>
                    <span className='dot'>&nbsp;</span>
                    {(warningCount > 0 ? "Warnings" : "Passed")}
                </span>
            </InstanceStatusContainer>
        </>
    }

    const renderSchemaIntegrityDetails = () => {
        let schemaRevision: string = previewData?.[InstanceManagementField.SCHEMA_REVISON] != null && previewData?.[InstanceManagementField.SCHEMA_REVISON] != "" ? previewData?.[InstanceManagementField.SCHEMA_REVISON] : "--";
        let tablesTotal: number = previewData?.[InstanceManagementField.TABLES_TOTAL] ?? 0;
        let tablesPresent: number = previewData?.[InstanceManagementField.TABLES_PRESENT] ?? 0;
        let indexesTotal: number = previewData?.[InstanceManagementField.INDEXES_TOTAL] ?? 0;
        let indexesValid: number = previewData?.[InstanceManagementField.INDEXES_VALID] ?? 0;
        let foreignKeysConstraints: number = previewData?.[InstanceManagementField.FOREIGN_KEYS_CONSTRAINTS] ?? 0;
        let validConstraints: number = previewData?.[InstanceManagementField.VALID_CONSTRAINTS] ?? 0;
        let orphanedRecordsCount: number = previewData?.[InstanceManagementField.ORPHANED_RECORDS_COUNT] ?? 0;

        return <>
            {renderSchemaIntegrityItem((tablesTotal == tablesPresent), false, 'Tables', `${tablesTotal} / ${tablesPresent} present ${previewData?.[InstanceManagementField.IS_ON_PREMISE] && `(baseline ${schemaRevision})`}`)}
            {renderSchemaIntegrityItem((indexesTotal == indexesValid), false, 'Indexes', `${indexesTotal} / ${indexesValid} valid`)}
            {renderSchemaIntegrityItem((foreignKeysConstraints > 0), false, 'Foreign Keys', `${foreignKeysConstraints} constraints enforced`)}
            {renderSchemaIntegrityItem((validConstraints > 0), false, 'Check Constraints', `${validConstraints} valid`)}
            {renderSchemaIntegrityItem((orphanedRecordsCount <= 0), (orphanedRecordsCount > 0), 'Orphaned Records', `${(orphanedRecordsCount > 0 ? `${orphanedRecordsCount} orphaned attendance rows (no employee)` : "None detected")}`)}
        
            <CustomButton
                btnType={ButtonType.PRIMARY_LIGHT}
                title='Re-run Schema Check'
                startIcon={<IconRefresh />}
                onClick={(e: any) => {
                    reRunSchemaCheck();
                }} />
        </>
    }

    const renderSchemaIntegrityItem = (isSuccess: boolean = false, isError: boolean = false, itemTitle: string, itemValue: string) => {
        return <SchemaIntegrityItem $darkMode={isDarkMode} sx={{borderColor: 'customBorder.main'}}>
            {isSuccess && <IconCircleCheck className='successIcon' />}
            {!isSuccess && !isError && <IconAlertTriangle className='warnIcon' />}
            {isError && <IconSquareRoundedX className='errorIcon' />}
            <div className='integrityItem'>
                <span className='integrityItemTitle'>{itemTitle}</span>
                <span className='integrityItemValue'>{itemValue}</span>
            </div>

            <InstanceStatusContainer $darkMode={isDarkMode} sx={{borderColor: 'customBorder.main'}} $noborderAndPadding={true} $itemOnRight={true}>
                <span className={'statusItemText ' + (isError ? "issues" : (!isSuccess ? "hasPendingMigrations" : "noPendingMigrations"))}>
                    <span className='dot'>&nbsp;</span>
                    {isError ? "Err" : (!isSuccess ? "Warn" : "Ok")}
                </span>
            </InstanceStatusContainer>
        </SchemaIntegrityItem>
    }
    ////////////////////////////////////////////////////////////////////////////////////////////////////

    //STORED PROCEDURES INTEGRITY
    const renderStoredProceduresIntegrityHeader = () => {
        return <InstanceDetailsPanelContainer>
            <IconCaretLeftRight />
            <div className='instanceHeaderDetails'>
                <span className='instanceName'>Stored Procedure Integrity</span>
            </div>
        </InstanceDetailsPanelContainer>
    }

    const renderStoredProceduresIntegrityActionBtns = () => {
        let warningCount: number = 0;

        return <>
            <CustomButton
                btnType={ButtonType.PRIMARY_LIGHT}
                title='Recompile All'
                disabled
                startIcon={<IconRefresh />}
                onClick={(e: any) => {
                    // reRunSchemaCheck();
                }} />
        </>
    }
    ////////////////////////////////////////////////////////////////////////////////////////////////////

    //AUTO BACKUP
    const renderAutoBackupHeader = () => {
        return <InstanceDetailsPanelContainer>
            <IconDeviceFloppy />
            <div className='instanceHeaderDetails'>
                <span className='instanceName'>Auto Backup</span>
            </div>
        </InstanceDetailsPanelContainer>
    }

    const renderAutoBackupActionBtns = () => {
        return <AutoBackupHeaderContainer $darkMode={isDarkMode}>
            <span className='enabledText'>{isEnabledAutoBackup ? "Enabled" : "Disabled"}</span>
            <CustomSwitch
                name='isEnabledAutoBackup'
                title=''
                switchType={ButtonType.PRIMARY}
                fieldValue={previewAutoBackupConfig?.[InstanceAutoBackupsField.ENABLED] ?? false}
                handleOnChange={(a: any) => {
                    setpreviewAutoBackupConfig(prev => ({ ...prev, [InstanceAutoBackupsField.ENABLED]: a }))
                    setautoBackupConfig(prev => ({ ...prev, [InstanceAutoBackupsField.ENABLED]: a }))
                    setsubmitForm(true);
                }}
                errorMessages={[]} />

            <CustomButton
                btnType={ButtonType.PRIMARY_LIGHT}
                title='Configure'
                startIcon={<IconSettings />}
                onClick={(e: any) => {
                    setopenBackupConfigModal(true);
                }} />
        </AutoBackupHeaderContainer>
    }

    const renderBackupConfigModal = () => {
        return <BackupConfigModal
            instanceName={previewData?.[InstanceManagementField.NAME] ?? "--"}
            autoBackupConfig={autoBackupConfig}
            previewAutoBackupConfig={previewAutoBackupConfig}
            setautoBackupConfig={setautoBackupConfig}
            setpreviewAutoBackupConfig={setpreviewAutoBackupConfig}
            openBackupConfigModal={openBackupConfigModal}
            setopenBackupConfigModal={setopenBackupConfigModal}
            handleSubmitBackupConfig={handleSubmitBackupConfig}
            DropdownData={DropdownData}
            dropdownFetchStatus={dropdownFetchStatus} />
    }
    ////////////////////////////////////////////////////////////////////////////////////////////////////

    //DB INSTANCES
    const renderDBInstancesHeader = () => {
        return <DbInstancesPanelContainer>
            <IconDatabase />
            <div className='instanceHeaderDetails'>
                <span className='instanceName'>Database Instances</span>
                
            </div>

            <span className='subTitle'>Spin up isolated, throwaway copies of the production database for debugging. Clones never affect live data.</span>
        </DbInstancesPanelContainer>
    }

    const renderDBInstancesActionBtns = () => {
        return <AutoBackupHeaderContainer $darkMode={isDarkMode}>
            <CustomButton
                btnType={ButtonType.PRIMARY_LIGHT}
                title='Refresh'
                startIcon={<IconRefresh />}
                onClick={async (e: any) => {
                    settriggeredQuery(CachingEnum.DB_INSTANCES_LIST);
                    await instancesInvalidateQueries([CachingEnum.DB_INSTANCES_LIST]);
                }} />

            <CustomButton
                className='cloneForDebugBtn'
                btnType={ButtonType.PRIMARY}
                title='Clone for Debug'
                startIcon={<DbCloneIcon />}
                onClick={(e: any) => {
                    setopenDBCloneModal(true);
                }} />
        </AutoBackupHeaderContainer>
    }

    const renderDBCloneModal = () => {
        return <DBCloneModal
            instanceName={previewData?.[InstanceManagementField.NAME] ?? "--"}
            openDBCloneModal={openDBCloneModal}
            setopenDBCloneModal={setopenDBCloneModal}
            handleCloneDB={handleCloneDB}
            DropdownData={DropdownData}
            dropdownFetchStatus={dropdownFetchStatus} />
    }
    ////////////////////////////////////////////////////////////////////////////////////////////////////

    //ACTIVITY LOGS
    const renderActivityLogsHeader = () => {
        return <InstanceDetailsPanelContainer>
            <IconActivityHeartbeat />
            <div className='instanceHeaderDetails'>
                <span className='instanceName'>Activity Log</span>
            </div>
        </InstanceDetailsPanelContainer>
    }
    ////////////////////////////////////////////////////////////////////////////////////////////////////

    

    const renderInstanceMngtDetails = () => {
        return <CustomDetailsCard
        isContentNoPadding
        title={renderInstanceHeaderDetails()}
        actionButtons={renderActionButtons()} >
            {renderInstanceOtherDetails()}
            {renderSummaryCounts()}
            {renderInstanceStatuses()}

            {openMaintenanceModeConfirmationModal && renderMaintenanceModeConfirmationModal()}
        </CustomDetailsCard>
    }

    const renderDBVersionAndMigrations = () => {
       
        return <CustomDetailsCard
        title={renderDBVersionAndMigrationsHeader()}
        actionButtons={renderDBVersionAndMigrationsActionBtns()} >
            {renderDBVersionAndMigrationsDetails()}
        </CustomDetailsCard>
    }

    const renderSchemaIntegrity = () => {
        return <CustomDetailsCard
        title={renderSchemaIntegrityHeader()}
        actionButtons={renderSchemaIntegrityActionBtns()} >
            {renderSchemaIntegrityDetails()}
        </CustomDetailsCard>
    }

    const renderStoredProceduresIntegrity = () => {
        return <CustomDetailsCard
        isContentNoPadding
        title={renderStoredProceduresIntegrityHeader()}
        actionButtons={renderStoredProceduresIntegrityActionBtns()} >
            <InstanceStoredProcedures
                triggeredQuery={triggeredQuery}
                settriggeredQuery={settriggeredQuery} />
        </CustomDetailsCard>
    }

    const renderAutoBackup = () => {
        return <CustomDetailsCard
        isContentNoPadding
        title={renderAutoBackupHeader()}
        actionButtons={renderAutoBackupActionBtns()} >
            <AutoBackups
                instanceName={previewData?.[InstanceManagementField.NAME] ?? "--"}
                autoBackupConfig={autoBackupConfig}
                setautoBackupConfig={setautoBackupConfig}
                previewAutoBackupConfig={previewAutoBackupConfig}
                setpreviewAutoBackupConfig={setpreviewAutoBackupConfig}
                triggeredQuery={triggeredQuery}
                settriggeredQuery={settriggeredQuery}
             />

            {openBackupConfigModal && renderBackupConfigModal()}
        </CustomDetailsCard>
    }

    const renderDBInstances = () => {
        return <CustomDetailsCard
        isContentNoPadding
        title={renderDBInstancesHeader()}
        actionButtons={renderDBInstancesActionBtns()} >
            <DBInstances
                instanceName={previewData?.[InstanceManagementField.NAME] ?? "--"}
                triggeredQuery={triggeredQuery}
                settriggeredQuery={settriggeredQuery}
             />

             {openDBCloneModal && renderDBCloneModal()}
        </CustomDetailsCard>
    }

    const renderActivityLogs = () => {
        return <CustomDetailsCard
        isContentNoPadding
        title={renderActivityLogsHeader()} >
            <InstanceActivityLogs
                triggeredQuery={triggeredQuery}
                settriggeredQuery={settriggeredQuery} />
        </CustomDetailsCard>
    }

    return <DetailsContainer>
        {renderInstanceMngtDetails()}

        <TwoItemsContainer>
            {renderDBVersionAndMigrations()}
            {renderSchemaIntegrity()}
        </TwoItemsContainer>
        
        {renderStoredProceduresIntegrity()}
        {renderAutoBackup()}
        {renderDBInstances()}
        {renderActivityLogs()}
    </DetailsContainer>
}
